import {
  PIN_EXTENDED_TYPES,
  type PinExtendedType,
  type PinExtendedTypes,
} from '@/microfrontends/map/shared/utils/mapModules/PinsLayer/types';

import { type ColorsConfig, type IPinService, type PinServiceInitOptions, type SizesConfig } from '../IPinService';

import {
  EXTENDED_POINT_BORDER_WIDTH,
  EXTENDED_POINT_RADIUS,
  POINT_BORDER_WIDTH,
  POINT_COLORS,
  POINT_COUNT_MAX_VALUE,
  POINT_COUNT_MIN_VALUE,
  POINT_GLOW_RADIUS,
  POINT_RADIUS,
  POINT_WITH_COUNT_BORDER_WIDTH,
  POINT_WITH_COUNT_RADIUS,
} from './constants';
import { FPinType } from './types';

export class PinService implements IPinService {
  private cluster;

  public constructor(parameters: PinServiceInitOptions) {
    this.cluster = parameters.cluster;
  }

  public provideId = (): string => {
    return this.cluster.id;
  };

  public provideListingId = (): string | undefined => {
    return this.cluster.properties?.id;
  };

  public get isPinInteractive(): boolean {
    return !!this.provideListingId();
  }

  public provideColors = (): ColorsConfig => {
    const { white, lightBlue, regularBlue, hoveredBlue, activeBlue, textBlue, glowBlue } = POINT_COLORS;

    const extendedTypes = this.provideExtendedTypes();
    const isViewed = extendedTypes.has(PIN_EXTENDED_TYPES.viewed);
    const isActive = extendedTypes.has(PIN_EXTENDED_TYPES.active);
    const isHovered = extendedTypes.has(PIN_EXTENDED_TYPES.hovered);
    const isExtended = this.provideType() === FPinType.Extended;

    /**
     * @description Пины рекомендованных объявлений
     */
    if (isExtended && !isActive) {
      return {
        fillColor: white,
        borderColor: isViewed ? lightBlue : regularBlue,
        textColor: textBlue,
      };
    }

    let fillColor = regularBlue;

    if (isViewed && !isActive) {
      fillColor = lightBlue;
    } else if (isActive) {
      fillColor = activeBlue;
    } else if (isHovered) {
      fillColor = hoveredBlue;
    }

    /** @description Пины остальных объявлений */
    return {
      fillColor,
      borderColor: white,
      textColor: white,
      glowPulsedColor: glowBlue,
    };
  };

  public provideCount = (): string => {
    const counter = this.cluster.properties?.counter || '';
    const numberCounter = Number(counter);

    if (numberCounter < POINT_COUNT_MIN_VALUE) {
      return '';
    }

    if (numberCounter > POINT_COUNT_MAX_VALUE) {
      return `${POINT_COUNT_MAX_VALUE}+`;
    }

    return counter;
  };

  public provideIsWithGlow = (): boolean => {
    const extendedTypes = this.provideExtendedTypes();

    return extendedTypes.has(PIN_EXTENDED_TYPES.glow);
  };

  public provideIsWithPulsedGlow = (): boolean => {
    const extendedTypes = this.provideExtendedTypes();

    return extendedTypes.has(PIN_EXTENDED_TYPES.pulsedGlow);
  };

  public provideIsWithBalloon = (): boolean => {
    const extendedTypes = this.provideExtendedTypes();

    return extendedTypes.has(PIN_EXTENDED_TYPES.withBalloon);
  };

  public provideIsPinWithCounter = (): boolean => {
    const withCounter = Boolean(this.provideCount());
    const withBalloon = this.provideIsWithBalloon();

    return withCounter && !withBalloon;
  };

  public provideIsWithShadow = (): boolean => {
    const withBalloon = this.provideIsWithBalloon();

    if (withBalloon) {
      return false;
    }

    return this.isPinInteractive;
  };

  public provideRadius = (): number => {
    const pinType = this.provideType();
    const pinWithCounter = this.provideIsPinWithCounter();

    if (pinWithCounter) {
      return POINT_WITH_COUNT_RADIUS;
    }

    return pinType === FPinType.Extended ? EXTENDED_POINT_RADIUS : POINT_RADIUS;
  };

  public provideBorderWidth = (): number => {
    const pinType = this.provideType();
    const pinWithCounter = this.provideIsPinWithCounter();

    if (pinWithCounter) {
      return POINT_WITH_COUNT_BORDER_WIDTH;
    }

    return pinType === FPinType.Extended ? EXTENDED_POINT_BORDER_WIDTH : POINT_BORDER_WIDTH;
  };

  public provideGlowRadius = (): number | undefined => {
    const withGlow = this.provideIsWithGlow();

    return withGlow ? POINT_GLOW_RADIUS : undefined;
  };

  public provideSizes = (): SizesConfig => {
    const radius = this.provideRadius();
    const borderWidth = this.provideBorderWidth();
    const glowRadius = this.provideGlowRadius();

    return {
      radius,
      borderWidth,
      glowRadius,
    };
  };

  public provideLabel = (): string | undefined => {
    return this.cluster.properties?.label;
  };

  public provideDetails = (): string | undefined => {
    return this.cluster.properties?.details;
  };

  public provideTitle = (): string | undefined => {
    return this.cluster.properties?.title;
  };

  public provideType = (): FPinType => {
    if (this.cluster.properties?.outside) {
      return FPinType.Extended;
    }

    return FPinType.Regular;
  };

  public provideExtendedTypes = (): PinExtendedTypes => {
    const { properties } = this.cluster;

    const pinExtendedTypes = new Set<PinExtendedType>();

    if (properties) {
      if (properties.counter) {
        pinExtendedTypes.add(PIN_EXTENDED_TYPES.withCount);
      }

      if (properties.active) {
        pinExtendedTypes.add(PIN_EXTENDED_TYPES.active);
      }

      if (properties.favorite) {
        pinExtendedTypes.add(PIN_EXTENDED_TYPES.favorite);
      }

      if (properties.viewed) {
        pinExtendedTypes.add(PIN_EXTENDED_TYPES.viewed);
      }

      if (properties.title && !properties.outside) {
        pinExtendedTypes.add(PIN_EXTENDED_TYPES.withBalloon);
      }

      if (properties.glow && !properties.favorite) {
        pinExtendedTypes.add(PIN_EXTENDED_TYPES.glow);
      }

      if (properties.pulsedGlow && !properties.active && !properties.viewed) {
        pinExtendedTypes.add(PIN_EXTENDED_TYPES.pulsedGlow);
      }

      if (properties.hovered) {
        pinExtendedTypes.add(PIN_EXTENDED_TYPES.hovered);
      }
    }

    return pinExtendedTypes;
  };
}
